/**
 * SIL-2482: device identity the run page can always explain.
 *
 * Two jobs, both pure and applied API-side when a stored discovery is turned into a view (never written back into the
 * discovery frozen at pairing, whose digest the plan was resolved against):
 *
 * 1. GPU model from the PCI vendor:device id. The bench image carries no pci.ids and no NVIDIA userspace at discovery, so an
 *    old or new agent reports `model: null` for a GPU without nvidia-smi. The table below fills it in with no image rebuild.
 *    Extend it here; it is keyed on the first two groups of the 4-group `pciId` (`vendor:device:subvendor:subdevice`).
 * 2. A reason for every missing serial / model, so the portal says why instead of an empty cell. A current agent records the
 *    reason in `readErrors` (field `serial` / `model`, reason one of {@link BENCH_MISSING_REASONS}); an old agent (0.1.0)
 *    recorded nothing for most gaps, so the same reason is derived here from the device kind and what was read.
 */

export const BENCH_MISSING_REASONS = [
  /** The firmware answered, but the field is empty (an unprogrammed board, a blank drive serial). */
  'blank_in_firmware',
  /** The firmware answered with a placeholder ("Not Specified", "Unknown") that is not a usable value. */
  'placeholder',
  /** The tool that reads the field is not on the host (e.g. nvidia-smi before the GPU pack loads). */
  'tool_unavailable',
  /** This part has no serial / model source the agent can read (CPU, NIC, AMD GPU serial). */
  'not_supported',
  /** The read itself failed (permission, I/O). */
  'unreadable',
  /** SIL-2499: a DIMM serial SMBIOS did not give, and no SPD image could be read (driver absent, SMBus locked by firmware or the BMC). */
  'spd_unavailable',
  /** SIL-2499: SPD images were read, but none pairs with this DIMM without guessing (identical modules, size mismatch). */
  'spd_unmatched',
] as const;

export type BenchMissingReason = (typeof BENCH_MISSING_REASONS)[number];

/** Customer-facing copy for a reason; one place so every portal reads the same. */
export const BENCH_MISSING_REASON_LABELS: Record<BenchMissingReason, string> = {
  blank_in_firmware: 'Blank in firmware',
  placeholder: 'Placeholder value',
  tool_unavailable: 'Tool unavailable',
  not_supported: 'Not supported',
  unreadable: 'Unreadable',
  spd_unavailable: 'SPD not readable',
  spd_unmatched: 'SPD could not be matched to this module',
};

const REASON_SET: ReadonlySet<string> = new Set(BENCH_MISSING_REASONS);

/**
 * Known GPUs by `vendor:device` (lowercase hex). Names follow pci.ids / the NVIDIA driver name table and the bench GPU
 * matrix profiles (db/schema 1139, 1196, 1247). A device id missing here simply keeps `model: null`.
 */
export const GPU_PCI_MODELS: Readonly<Record<string, string>> = {
  // NVIDIA Pascal
  '10de:15f7': 'Tesla P100 PCIe 12GB',
  '10de:15f8': 'Tesla P100 PCIe 16GB',
  '10de:15f9': 'Tesla P100 SXM2 16GB',
  // NVIDIA Volta
  '10de:1db1': 'Tesla V100 SXM2 16GB',
  '10de:1db4': 'Tesla V100 PCIe 16GB',
  '10de:1db5': 'Tesla V100 SXM2 32GB',
  '10de:1db6': 'Tesla V100 PCIe 32GB',
  '10de:1db8': 'Tesla V100 SXM3 32GB',
  '10de:1df6': 'Tesla V100S PCIe 32GB',
  // NVIDIA Turing
  '10de:1eb8': 'Tesla T4',
  // NVIDIA Ampere
  '10de:20b0': 'A100 SXM4 40GB',
  '10de:20b2': 'A100 SXM4 80GB',
  '10de:20b5': 'A100 PCIe 80GB',
  '10de:20f1': 'A100 PCIe 40GB',
  '10de:2235': 'A40',
  '10de:2236': 'A10',
  // NVIDIA Hopper
  '10de:2321': 'H100 NVL',
  '10de:2330': 'H100 SXM5 80GB',
  '10de:2331': 'H100 PCIe',
  '10de:2335': 'H200 SXM',
  '10de:233b': 'H200 NVL',
  // NVIDIA Ada
  '10de:26b5': 'L40',
  '10de:26b9': 'L40S',
  '10de:27b8': 'L4',
  // NVIDIA Blackwell
  '10de:2901': 'B200',
  // AMD
  '1002:6608': 'FirePro W2100',
  '1002:738c': 'Instinct MI100',
  '1002:740c': 'Instinct MI250X',
  '1002:740f': 'Instinct MI210',
  '1002:74a1': 'Instinct MI300X',
};

/** `vendor:device` of a 4-group pci id (`10de:1db1:10de:1212`), lowercased; null when it is not that shape. */
export function pciVendorDevice(pciId: string | null | undefined): string | null {
  if (!pciId) return null;
  const m = /^([0-9a-f]{4}):([0-9a-f]{4})(?::[0-9a-f]{4}:[0-9a-f]{4})?$/i.exec(pciId.trim());
  return m ? `${m[1]}:${m[2]}`.toLowerCase() : null;
}

/** The model name for a GPU's pci id, or null when the table does not know it. */
export function gpuModelFromPciId(pciId: string | null | undefined): string | null {
  const key = pciVendorDevice(pciId);
  return key ? (GPU_PCI_MODELS[key] ?? null) : null;
}

export interface IdentityDevice {
  kind: string;
  pciId?: string | null;
  serial: string | null;
  model: string | null;
  readErrors?: ReadonlyArray<{ field: string; reason: string }>;
}

function explicitReason(d: IdentityDevice, fields: readonly string[]): BenchMissingReason | null {
  for (const e of d.readErrors ?? []) {
    if (!fields.includes(e.field)) continue;
    const code = e.reason.split(':')[0]!.trim();
    if (REASON_SET.has(code)) return code as BenchMissingReason;
  }
  return null;
}

/** The model to show: what the agent read, else the PCI-id table for a GPU. `source` says which. */
export function resolveDeviceModel(d: IdentityDevice): { model: string | null; source: 'agent' | 'pci_id' | null } {
  if (d.model !== null) return { model: d.model, source: 'agent' };
  if (d.kind === 'gpu') {
    const model = gpuModelFromPciId(d.pciId);
    if (model !== null) return { model, source: 'pci_id' };
  }
  return { model: null, source: null };
}

/** Why `d.serial` is null; null when a serial is present. Always a reason when it is missing. */
export function missingSerialReason(d: IdentityDevice): BenchMissingReason | null {
  if (d.serial !== null) return null;
  // The chassis serial is the DMI product serial: its read error is keyed by the sysfs file name.
  const fields = d.kind === 'chassis' ? ['serial', 'product_serial'] : ['serial'];
  const explicit = explicitReason(d, fields);
  if (explicit) return explicit;
  if (d.kind === 'chassis' && (d.readErrors ?? []).some((e) => e.field === 'product_serial')) return 'unreadable';
  switch (d.kind) {
    case 'chassis':
    case 'drive':
    case 'nvme':
      // The file / field was read without error and held nothing.
      return 'blank_in_firmware';
    case 'dimm':
      // An agent that predates SIL-2482 dropped "Not Specified" / "Unknown" silently.
      return 'placeholder';
    default:
      // gpu (AMD, or NVIDIA with nvidia-smi answering [N/A]), cpu, nic, psu: no serial source.
      return 'not_supported';
  }
}

/** Why the model is null after {@link resolveDeviceModel}; null when a model is known. Always a reason when it is missing. */
export function missingModelReason(d: IdentityDevice): BenchMissingReason | null {
  if (resolveDeviceModel(d).model !== null) return null;
  const explicit = explicitReason(d, ['model']);
  if (explicit) return explicit;
  switch (d.kind) {
    case 'gpu':
      // Agent 0.1.0 recorded only the serial read error; the model came from the same absent tool.
      return explicitReason(d, ['serial']) === 'tool_unavailable' ? 'tool_unavailable' : 'not_supported';
    case 'chassis':
    case 'drive':
    case 'nvme':
    case 'dimm':
      return 'blank_in_firmware';
    default:
      return 'not_supported';
  }
}
